import React, { useState, useEffect, useCallback } from 'react';
import { BarChart2, TrendingUp, Eye, Heart, Play, Monitor, Users, Globe, RefreshCw, Radio, MapPin } from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n) => {
    if (n === null || n === undefined) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
};

const fmtDate = (d) => {
    const dt = new Date(d);
    return `${dt.getMonth() + 1}/${dt.getDate()}`;
};

// ISO-2 country code -> display name, using the browser's built-in Intl API
// (no lookup table / extra dependency needed).
let regionNames;
try {
    regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch (e) {
    regionNames = null;
}
const countryName = (code) => {
    if (!code) return 'Unknown';
    try {
        return (regionNames && regionNames.of(code.toUpperCase())) || code;
    } catch (e) {
        return code;
    }
};

// City + state where we have both (from IP geolocation), falling back to
// city + country, then just country, for visitors outside the US or with
// partial data.
const visitorLocation = (v) => {
    const cityState = [v.city, v.region].filter(Boolean).join(', ');
    if (cityState) return cityState;
    if (v.city) return v.city;
    if (v.country) return countryName(v.country);
    return 'Unknown location';
};

const DEVICE_COLORS = { mobile: '#f472b6', tablet: '#fbbf24', desktop: '#60a5fa', unknown: '#6b7280' };

// World outline for the live map — world-atlas topojson from jsdelivr's CDN.
// No API key or tile server needed; this is just a static geography file.
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const PAGE_NAMES = {
    '/':               'Home',
    '/feed':           'Feed',
    '/feed/audio':     'Audio Feed',
    '/feed/videos':    'Video Feed',
    '/feed/images':    'Image Feed',
    '/feed/posts':     'Posts Feed',
    '/discover':       'Discover',
    '/gallery':        'Gallery',
    '/community':       'Community',
    '/calendar':       'Calendar',
    '/subscriptions':  'Subscriptions',
    '/login':          'Login',
    '/register':       'Register',
    '/privacy':        'Privacy Policy',
    '/terms':          'Terms of Service',
    '/support':        'Support',
    '/sitemap':        'Sitemap',
    '/dashboard':      'Dashboard',
    '/account':        'Account',
    '/following':      'Following',
};

const getPageName = (row) => {
    const { page_identifier: id, page_type: type } = row;
    if (type === 'profile') return `@${id}`;
    if (type === 'media')   return row.media_title || `Media #${id}`;
    if (PAGE_NAMES[id])     return PAGE_NAMES[id];
    if (id?.startsWith('/profile/'))        return `@${id.replace('/profile/', '')}`;
    if (id?.startsWith('/feed/artist/'))    return `${id.split('/')[3]}'s Feed`;
    if (id?.startsWith('/gallery/artist/')) return `${id.split('/gallery/artist/')[1]}'s Gallery`;
    if (id?.startsWith('/events/'))         return `Event #${id.split('/events/')[1]}`;
    if (id?.startsWith('/view/'))           return `${id.split('/')[2]} — ${id.split('/')[3]}`;
    return id || 'Unknown';
};

const getPageUrl = (row) => {
    const { page_identifier: id, page_type: type } = row;
    if (type === 'profile') return `/profile/${id}`;
    if (type === 'media' && row.media_username && row.media_filename && row.media_file_type)
        return `/view/${row.media_file_type}/${row.media_username}/${row.media_filename}`;
    if (type === 'other') return id; // id IS the path
    return null;
};

// ─── Shared recharts styling ────────────────────────────────────────────────

const DarkTooltip = ({ active, payload, label, labelFormatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 shadow-xl">
            <div className="text-xs text-gray-400 mb-1">{labelFormatter ? labelFormatter(label) : label}</div>
            {payload.map((p, i) => (
                <div key={i} className="text-sm text-white font-medium">
                    {p.name ? <span className="text-gray-400 capitalize">{p.name}: </span> : ''}{fmt(p.value)}
                </div>
            ))}
        </div>
    );
};

const TimeSeriesChart = ({ data = [], color = '#60a5fa' }) => {
    if (!data.length) return <div className="flex items-center justify-center h-44 text-gray-500 text-sm">No data yet</div>;
    const gradId = `grad-${color.replace('#', '')}`;
    return (
        <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                <Tooltip content={<DarkTooltip labelFormatter={fmtDate} />} />
                <Area type="monotone" dataKey="count" stroke={color} fill={`url(#${gradId})`} strokeWidth={2} />
            </AreaChart>
        </ResponsiveContainer>
    );
};

const HorizontalBarChart = ({ data = [], color = '#818cf8', labelFormatter, barHeight = 32, minHeight = 120 }) => {
    if (!data.length) return <p className="text-gray-500 text-sm">No data yet</p>;
    return (
        <ResponsiveContainer width="100%" height={Math.max(minHeight, data.length * barHeight)}>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                <XAxis type="number" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis
                    type="category"
                    dataKey="label"
                    tickFormatter={labelFormatter}
                    stroke="#9ca3af"
                    fontSize={12}
                    width={100}
                    tickLine={false}
                    axisLine={false}
                />
                <Tooltip content={<DarkTooltip labelFormatter={labelFormatter} />} />
                <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, total, period, periodLabel, color }) => (
    <div className="glass-card p-5">
        <div className="flex items-start justify-between mb-3">
            <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-gray-500">{periodLabel}</span>
        </div>
        <div className="text-2xl font-bold text-white mb-1">{fmt(period)}</div>
        <div className="text-sm text-gray-400">{label}</div>
        <div className="text-xs text-gray-500 mt-1">All time: {fmt(total)}</div>
    </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
    { id: '1d',   label: 'Last Day' },
    { id: '7d',   label: 'Last 7 Days' },
    { id: '30d',  label: 'Last 30 Days' },
    { id: '90d',  label: 'Last 90 Days' },
    { id: '180d', label: 'Last 180 Days' },
    { id: '365d', label: 'Last 365 Days' },
    { id: 'all',  label: 'All Time' },
];

const MAP_RANGE_OPTIONS = [
    { id: 'live', label: 'Live' },
    { id: '1d',   label: '1 Day' },
    { id: '7d',   label: '7 Days' },
    { id: '30d',  label: '30 Days' },
];

const ACTIVE_CARD_COPY = {
    live: { title: 'Active Now', subtitle: 'Visitors seen in the last 5 minutes' },
    '1d': { title: 'Visitors',   subtitle: 'Seen in the last 24 hours' },
    '7d': { title: 'Visitors',   subtitle: 'Seen in the last 7 days' },
    '30d': { title: 'Visitors',  subtitle: 'Seen in the last 30 days' },
};

const AdminAnalytics = () => {
    const [period, setPeriod] = useState('30d');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [overview, setOverview] = useState(null);
    const [viewsChart, setViewsChart] = useState([]);
    const [playsChart, setPlaysChart] = useState([]);
    const [likesChart, setLikesChart] = useState([]);
    const [topPages, setTopPages] = useState([]);
    const [topMedia, setTopMedia] = useState([]);
    const [devices, setDevices] = useState({ devices: [], browsers: [], oses: [] });
    const [userBreakdown, setUserBreakdown] = useState(null);
    const [geoData, setGeoData] = useState([]);

    // "Active now" is independent of the period selector (it's always "right now")
    // and is derived from ordinary page-view tracking — no heartbeat/ping involved.
    // It loads once on mount and otherwise only updates via its own Refresh button.
    const [activeNow, setActiveNow] = useState({ count: 0, visitors: [] });
    const [activeNowLoading, setActiveNowLoading] = useState(true);
    const [activeNowRefreshing, setActiveNowRefreshing] = useState(false);

    const fetchAll = useCallback(async (showRefresh = false) => {
        if (showRefresh) setRefreshing(true);
        else setLoading(true);
        try {
            const p = `?period=${period}`;
            const [ov, v, pl, li, tp, tm, dv, ub, geo] = await Promise.all([
                api.get(`/admin/analytics/overview${p}`),
                api.get(`/admin/analytics/views-over-time${p}`),
                api.get(`/admin/analytics/plays-over-time${p}`),
                api.get(`/admin/analytics/likes-over-time${p}`),
                api.get(`/admin/analytics/top-pages${p}&limit=10`),
                api.get(`/admin/analytics/top-media${p}&limit=10`),
                api.get(`/admin/analytics/devices${p}`),
                api.get(`/admin/analytics/users${p}`),
                api.get(`/admin/analytics/geo${p}`),
            ]);
            if (ov.data.success)  setOverview(ov.data.data);
            if (v.data.success)   setViewsChart(v.data.data);
            if (pl.data.success)  setPlaysChart(pl.data.data);
            if (li.data.success)  setLikesChart(li.data.data);
            if (tp.data.success)  setTopPages(tp.data.data);
            if (tm.data.success)  setTopMedia(tm.data.data);
            if (dv.data.success) {
                // MySQL's SUM() comes back as a string — coerce to Number so recharts/d3
                // do real arithmetic on these instead of accidentally string-concatenating.
                setDevices({
                    devices:  (dv.data.data.devices  || []).map(d => ({ ...d, count: Number(d.count) })),
                    browsers: (dv.data.data.browsers || []).map(b => ({ ...b, count: Number(b.count) })),
                    oses:     (dv.data.data.oses     || []).map(o => ({ ...o, count: Number(o.count) })),
                });
            }
            if (ub.data.success)  setUserBreakdown(ub.data.data);
            if (geo.data.success) setGeoData(geo.data.data);
        } catch (err) {
            console.error('Analytics fetch error:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [period]);

    const fetchActiveNow = useCallback(async (showRefresh = false) => {
        if (showRefresh) setActiveNowRefreshing(true);
        else setActiveNowLoading(true);
        try {
            const res = await api.get('/admin/analytics/active-now');
            if (res.data.success) setActiveNow(res.data.data);
        } catch (err) {
            console.error('Active now fetch error:', err);
        } finally {
            setActiveNowLoading(false);
            setActiveNowRefreshing(false);
        }
    }, []);

    // The map panel has its own Live / 1D / 7D / 30D toggle, independent of the
    // "Active Now" stat card (which always stays live) and the page-wide period
    // selector above. "Live" reuses activeNow's data instead of a separate fetch.
    const [mapRange, setMapRange] = useState('live');
    const [mapVisitors, setMapVisitors] = useState([]);
    const [mapLoading, setMapLoading] = useState(false);

    const fetchMapVisitors = useCallback(async (range) => {
        if (range === 'live') return;
        setMapLoading(true);
        try {
            const res = await api.get(`/admin/analytics/visitor-map?range=${range}`);
            if (res.data.success) setMapVisitors(res.data.data.visitors);
        } catch (err) {
            console.error('Visitor map fetch error:', err);
        } finally {
            setMapLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { fetchActiveNow(); }, [fetchActiveNow]);
    useEffect(() => { fetchMapVisitors(mapRange); }, [mapRange, fetchMapVisitors]);

    const periodLabel = PERIOD_OPTIONS.find(p => p.id === period)?.label || 'Last 30 Days';

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900">
                <PageHeader title="Analytics" subtitle="Loading data…" icon={<BarChart2 className="w-8 h-8" />} />
                <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-center h-64">
                    <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
            </div>
        );
    }

    const maxTopPage = Math.max(...topPages.map(p => p.views), 1);

    // MySQL's SUM() comes back as a string — coerce to Number so the totals below
    // add instead of concatenating (see same fix on the `devices` breakdown above).
    const viewLoggedIn = Number(userBreakdown?.views?.loggedIn) || 0;
    const viewAnon = Number(userBreakdown?.views?.anonymous) || 0;
    const viewTotal = viewLoggedIn + viewAnon || 1;
    const playLoggedIn = Number(userBreakdown?.plays?.loggedIn) || 0;
    const playAnon = Number(userBreakdown?.plays?.anonymous) || 0;
    const playTotal = playLoggedIn + playAnon || 1;

    const mapSourceVisitors = mapRange === 'live' ? (activeNow.visitors || []) : mapVisitors;
    const mappableVisitors = mapSourceVisitors.filter(v => v.latitude != null && v.longitude != null);
    const mapIsLoading = mapRange === 'live' ? activeNowLoading : mapLoading;

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader
                title="Analytics Dashboard"
                subtitle={`Internal tracking data • ${periodLabel}`}
                icon={<BarChart2 className="w-8 h-8" />}
            />

            <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

                {/* Controls */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                        {PERIOD_OPTIONS.map(({ id, label }) => (
                            <button
                                key={id}
                                onClick={() => setPeriod(id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    period === id
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => fetchAll(true)}
                        disabled={refreshing}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-400 hover:bg-gray-700 rounded-lg text-sm transition-colors self-start sm:self-auto"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>

                {/* Overview cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={Eye}        label="Page Views"      total={overview?.pageViews?.total}      period={overview?.pageViews?.period}      periodLabel={periodLabel} color="bg-blue-600" />
                    <StatCard icon={Play}       label="Media Plays"     total={overview?.plays?.total}          period={overview?.plays?.period}          periodLabel={periodLabel} color="bg-purple-600" />
                    <StatCard icon={Heart}      label="Likes"           total={overview?.likes?.total}          period={overview?.likes?.period}          periodLabel={periodLabel} color="bg-pink-600" />
                    <StatCard icon={Users}      label="Unique Visitors" total={overview?.uniqueVisitors?.total} period={overview?.uniqueVisitors?.period} periodLabel={periodLabel} color="bg-green-600" />
                </div>

                {/* Active Now + Live Map */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="glass-card p-5">
                        <div className="flex items-start justify-between mb-3">
                            <div className="p-2 rounded-lg bg-emerald-600">
                                <Radio className="w-5 h-5 text-white" />
                            </div>
                            <button
                                onClick={() => mapRange === 'live' ? fetchActiveNow(true) : fetchMapVisitors(mapRange)}
                                disabled={mapIsLoading}
                                className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                                title="Refresh visitors"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${mapIsLoading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                            {mapRange === 'live' && (
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                                </span>
                            )}
                            <div className="text-2xl font-bold text-white">{mapIsLoading ? '—' : mapSourceVisitors.length}</div>
                        </div>
                        <div className="text-sm text-gray-400">{ACTIVE_CARD_COPY[mapRange].title}</div>
                        <div className="text-xs text-gray-500 mt-1">{ACTIVE_CARD_COPY[mapRange].subtitle}</div>
                        {mapSourceVisitors.length > 0 && (
                            <div className="flex gap-3 mt-4 pt-4 border-t border-gray-700/50">
                                {['desktop', 'mobile', 'tablet'].map(type => {
                                    const n = mapSourceVisitors.filter(v => (v.device_type || 'unknown') === type).length;
                                    if (!n) return null;
                                    return (
                                        <div key={type} className="text-center flex-1">
                                            <div className="text-sm font-semibold" style={{ color: DEVICE_COLORS[type] }}>{n}</div>
                                            <div className="text-[10px] text-gray-500 capitalize">{type}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {mapSourceVisitors.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-1.5 max-h-36 overflow-y-auto">
                                {mapSourceVisitors.map((v, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs">
                                        <span
                                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: DEVICE_COLORS[v.device_type || 'unknown'] }}
                                        />
                                        <span className="text-gray-400 truncate flex-1">{visitorLocation(v)}</span>
                                        <span
                                            className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                v.is_returning
                                                    ? 'bg-blue-500/10 text-blue-400'
                                                    : 'bg-amber-500/10 text-amber-400'
                                            }`}
                                            title={v.is_returning ? 'Seen on the site before' : 'First time seen'}
                                        >
                                            {v.is_returning ? 'Returning' : 'New'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="glass-card p-6 lg:col-span-2">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <h3 className="text-white font-semibold flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-emerald-400" /> Visitor Map
                            </h3>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
                                    {MAP_RANGE_OPTIONS.map(({ id, label }) => (
                                        <button
                                            key={id}
                                            onClick={() => setMapRange(id)}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                                                mapRange === id
                                                    ? 'bg-emerald-600 text-white'
                                                    : 'text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => mapRange === 'live' ? fetchActiveNow(true) : fetchMapVisitors(mapRange)}
                                    disabled={mapIsLoading}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-gray-400 hover:bg-gray-700 rounded-lg text-xs transition-colors"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${mapIsLoading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </button>
                            </div>
                        </div>
                        <div className="relative">
                            <ComposableMap
                                projection="geoEqualEarth"
                                projectionConfig={{ scale: 130 }}
                                width={800}
                                height={380}
                                style={{ width: '100%', height: 'auto' }}
                            >
                                <Geographies geography={GEO_URL}>
                                    {({ geographies }) =>
                                        geographies.map(geo => (
                                            <Geography
                                                key={geo.rsmKey}
                                                geography={geo}
                                                fill="#1f2937"
                                                stroke="#374151"
                                                strokeWidth={0.5}
                                                style={{
                                                    default: { outline: 'none' },
                                                    hover: { outline: 'none', fill: '#273449' },
                                                    pressed: { outline: 'none' },
                                                }}
                                            />
                                        ))
                                    }
                                </Geographies>
                                {mappableVisitors.map((v, i) => (
                                    <Marker key={i} coordinates={[v.longitude, v.latitude]}>
                                        <circle
                                            r={4}
                                            fill={DEVICE_COLORS[v.device_type] || '#818cf8'}
                                            stroke="#111827"
                                            strokeWidth={1}
                                            opacity={0.9}
                                        >
                                            <title>
                                                {[v.city, countryName(v.country)].filter(Boolean).join(', ') || 'Unknown location'}
                                                {' • '}{v.device_type || 'unknown'}
                                            </title>
                                        </circle>
                                    </Marker>
                                ))}
                            </ComposableMap>
                            {!mapIsLoading && mapSourceVisitors.length === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <span className="text-gray-500 text-sm bg-gray-900/70 px-3 py-1.5 rounded-lg">
                                        {mapRange === 'live' ? 'No active visitors right now' : 'No visitors in this range'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Charts row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Eye className="w-5 h-5 text-blue-400" /> Page Views Over Time
                        </h3>
                        <TimeSeriesChart data={viewsChart} color="#60a5fa" />
                    </div>
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Play className="w-5 h-5 text-purple-400" /> Media Plays Over Time
                        </h3>
                        <TimeSeriesChart data={playsChart} color="#a78bfa" />
                    </div>
                </div>

                {/* Charts row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-pink-400" /> Likes Over Time
                        </h3>
                        <TimeSeriesChart data={likesChart} color="#f472b6" />
                    </div>

                    {/* Device breakdown */}
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-green-400" /> Device Breakdown
                        </h3>
                        {devices.devices.length > 0 ? (
                            <div className="mb-2">
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie data={devices.devices} dataKey="count" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                                            {devices.devices.map((d, i) => <Cell key={i} fill={DEVICE_COLORS[d.label] || '#818cf8'} />)}
                                        </Pie>
                                        <Tooltip content={<DarkTooltip />} />
                                        <Legend formatter={(value) => <span className="text-gray-300 text-xs capitalize">{value}</span>} iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm mb-4">No data yet</p>
                        )}
                        <div className="mb-4">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Browsers</div>
                            <HorizontalBarChart data={devices.browsers} color="#34d399" />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Operating Systems</div>
                            <HorizontalBarChart data={devices.oses} color="#fbbf24" />
                        </div>
                    </div>
                </div>

                {/* Visitors by country */}
                <div className="glass-card p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-cyan-400" /> Visitors by Country ({periodLabel})
                    </h3>
                    <HorizontalBarChart
                        data={geoData.slice(0, 10).map(g => ({ label: g.country, count: Number(g.views) }))}
                        color="#22d3ee"
                        labelFormatter={countryName}
                        barHeight={36}
                    />
                </div>

                {/* Logged-in vs anonymous */}
                {userBreakdown && (
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Globe className="w-5 h-5 text-indigo-400" /> Logged-in vs Anonymous ({periodLabel})
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <div className="text-sm text-gray-400 mb-3">Page Views</div>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-gray-300 text-sm w-28">Logged-in</span>
                                    <div className="flex-1 bg-gray-700 rounded-full h-3">
                                        <div className="h-3 rounded-full bg-blue-500" style={{ width: `${(viewLoggedIn / viewTotal) * 100}%` }} />
                                    </div>
                                    <span className="text-gray-400 text-sm w-16 text-right">{fmt(viewLoggedIn)} ({Math.round((viewLoggedIn / viewTotal) * 100)}%)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-gray-300 text-sm w-28">Anonymous</span>
                                    <div className="flex-1 bg-gray-700 rounded-full h-3">
                                        <div className="h-3 rounded-full bg-gray-500" style={{ width: `${(viewAnon / viewTotal) * 100}%` }} />
                                    </div>
                                    <span className="text-gray-400 text-sm w-16 text-right">{fmt(viewAnon)} ({Math.round((viewAnon / viewTotal) * 100)}%)</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-gray-400 mb-3">Media Plays</div>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-gray-300 text-sm w-28">Logged-in</span>
                                    <div className="flex-1 bg-gray-700 rounded-full h-3">
                                        <div className="h-3 rounded-full bg-purple-500" style={{ width: `${(playLoggedIn / playTotal) * 100}%` }} />
                                    </div>
                                    <span className="text-gray-400 text-sm w-16 text-right">{fmt(playLoggedIn)} ({Math.round((playLoggedIn / playTotal) * 100)}%)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-gray-300 text-sm w-28">Anonymous</span>
                                    <div className="flex-1 bg-gray-700 rounded-full h-3">
                                        <div className="h-3 rounded-full bg-gray-500" style={{ width: `${(playAnon / playTotal) * 100}%` }} />
                                    </div>
                                    <span className="text-gray-400 text-sm w-16 text-right">{fmt(playAnon)} ({Math.round((playAnon / playTotal) * 100)}%)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tables row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top pages */}
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-blue-400" /> Top Pages ({periodLabel})
                        </h3>
                        <div className="space-y-3">
                            {topPages.length === 0 && <p className="text-gray-500 text-sm">No data yet</p>}
                            {topPages.map((p, i) => {
                                const name = getPageName(p);
                                const url  = getPageUrl(p);
                                return (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="text-gray-500 text-xs w-5 flex-shrink-0">{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                {url ? (
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-400 hover:text-blue-300 hover:underline truncate transition-colors"
                                                    >
                                                        {name}
                                                    </a>
                                                ) : (
                                                    <span className="text-sm text-gray-200 truncate">{name}</span>
                                                )}
                                                <span className="text-xs text-gray-600 flex-shrink-0 capitalize">{p.page_type}</span>
                                            </div>
                                            <div className="w-full bg-gray-700 rounded-full h-1">
                                                <div className="h-1 rounded-full bg-blue-500" style={{ width: `${(p.views / maxTopPage) * 100}%` }} />
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm text-white font-medium">{fmt(p.views)}</div>
                                            <div className="text-xs text-gray-500">{fmt(p.unique_visitors)} uniq</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top media */}
                    <div className="glass-card p-6">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Play className="w-5 h-5 text-purple-400" /> Top Media ({periodLabel})
                        </h3>
                        <div className="space-y-3">
                            {topMedia.length === 0 && <p className="text-gray-500 text-sm">No data yet</p>}
                            {topMedia.map((m, i) => {
                                const maxPlays = Math.max(...topMedia.map(x => x.plays), 1);
                                const mediaUrl = m.username && m.stored_filename && m.file_type
                                    ? `/view/${m.file_type}/${m.username}/${m.stored_filename}`
                                    : null;
                                const isDeleted = m.status === 'deleted';
                                return (
                                    <div key={m.id} className="flex items-center gap-3">
                                        <span className="text-gray-500 text-xs w-5 flex-shrink-0">{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                {mediaUrl && !isDeleted ? (
                                                    <a
                                                        href={mediaUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-blue-400 hover:text-blue-300 hover:underline truncate transition-colors"
                                                    >
                                                        {m.title || 'Untitled'}
                                                    </a>
                                                ) : (
                                                    <span className={`text-sm truncate ${isDeleted ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                                                        {m.title || 'Untitled'}
                                                    </span>
                                                )}
                                                <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 font-medium ${
                                                    isDeleted
                                                        ? 'bg-red-900/50 text-red-400'
                                                        : 'bg-green-900/50 text-green-400'
                                                }`}>
                                                    {isDeleted ? 'Deleted' : 'Active'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 mb-1">{m.display_name || m.username} • {m.file_type}</div>
                                            <div className="w-full bg-gray-700 rounded-full h-1">
                                                <div className="h-1 rounded-full bg-purple-500" style={{ width: `${(m.plays / maxPlays) * 100}%` }} />
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm text-white font-medium">{fmt(m.plays)}</div>
                                            <div className="text-xs text-gray-500">{fmt(m.unique_plays)} uniq</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminAnalytics;
